import { Popover } from 'antd';
import { Panel } from 'components/LightConversation/components/Ticket/Panel';
import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { css, cx } from '@emotion/css';
import { useSize } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { conv2TradeExternalBuyerId } from 'utils/search';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { RelatedOrderItems } from './RelatedOrderItems';
import { SelectMenu } from './SelectMenu';
import { Status } from './Status';
import { getPopupContainer } from 'components/LightConversation/utils/popover';
import { useHisenseFeature } from 'hisense/featureFlag';

interface PureRelatedOrdersProps {
  canEdit?: boolean;
  relatedTradeId?: string;
  autoSelectFirst?: boolean;
  platform: General.PlatformUpperCase;
  externalBuyerId: string;
  storeId: string;
  onSelect: (trade: Conversation.Trade.Vo | null) => void;
  onClear: () => void;
}

export const PureRelatedOrders = (props: PureRelatedOrdersProps) => {
  const {
    canEdit,
    relatedTradeId,
    autoSelectFirst,
    platform,
    externalBuyerId,
    storeId,
    onSelect,
    onClear,
  } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const rect = useSize(ref);
  const hasAutoSelectedRef = useRef(false);

  const { data } = useQuery({
    ...queries.orders.list({
      internal_store_id: storeId,
      external_buyer_id: conv2TradeExternalBuyerId(platform, externalBuyerId),
      page_size: 9999,
      page_no: 1,
    }),
  });

  const tradeList = useMemo(() => data?.tradeList ?? [], [data]);
  const getTrade = useCallback(
    (tradeId: string) => tradeList.find((trade) => trade.externalTradeId === tradeId),
    [tradeList],
  );
  const selectedTrade = useMemo(
    () => (relatedTradeId ? getTrade(relatedTradeId) : null),
    [relatedTradeId, getTrade],
  );

  const isHisense = useHisenseFeature();

  const { data: logisticsResponse } = useQuery({
    ...queries.orders.logistics({
      internal_trade_ids: selectedTrade?.internalTradeId || '',
    }),
    enabled: isHisense && !!selectedTrade?.internalTradeId,
  });

  const logisticsData = logisticsResponse?.data;

  const extShipmentId = useMemo(() => {
    if (!isHisense || !selectedTrade?.internalTradeId) return undefined;
    return logisticsData?.thirdTrackingInfo?.find(
      (t) => t.internalTradeId === selectedTrade.internalTradeId,
    )?.extShipmentId;
  }, [isHisense, selectedTrade?.internalTradeId, logisticsData]);

  useEffect(() => {
    if (!autoSelectFirst || hasAutoSelectedRef.current) return;
    if (relatedTradeId) return;
    if (!tradeList.length) return;
    onSelect(tradeList[0]);
    hasAutoSelectedRef.current = true;
  }, [autoSelectFirst, relatedTradeId, tradeList, onSelect]);

  const handleSelectOrder = useCallback(
    (trade: Conversation.Trade.Vo | null) => {
      onSelect(trade);
    },
    [onSelect],
  );

  const handleClearOrder = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear();
    },
    [onClear],
  );

  const selectedItems = selectedTrade?.items ?? [];
  const popoverContentWidth = rect?.width ?? 0;
  const isSelected = Boolean(relatedTradeId && selectedTrade);

  let content = null;
  if (canEdit) {
    if (tradeList.length === 0) {
      content = (
        <div
          className="p-2 flex items-center justify-center"
          style={{ width: popoverContentWidth }}
        >
          <div className="text-[var(--fett-text-tertiary)]">{t('暂无订单')}</div>
        </div>
      );
    } else {
      content = (
        <SelectMenu
          width={popoverContentWidth}
          tradeList={tradeList}
          relatedTradeId={relatedTradeId ?? ''}
          onSelect={handleSelectOrder}
          onClose={() => setVisible(false)}
        />
      );
    }
  }

  return (
    <Panel title={t('tab.trade')}>
      <Popover
        visible={visible}
        onVisibleChange={setVisible}
        trigger="click"
        content={content}
        arrowContent={null}
        placement="bottom"
        overlayClassName={popCls}
        getPopupContainer={getPopupContainer}
      >
        {isSelected ? (
          <div
            ref={ref}
            className={cx(
              'group w-full text-xs flex items-center justify-between cursor-pointer bg-[var(--fett-bg-subtle)] p-3 rounded',
              canEdit && 'cursor-pointer',
            )}
            onClick={() => canEdit && setVisible(true)}
            role={canEdit ? 'button' : undefined}
            tabIndex={canEdit ? 0 : undefined}
            onKeyDown={canEdit ? (e) => e.key === 'Enter' && setVisible(true) : undefined}
          >
            <div>
              <div className="flex items-center gap-1">
                {selectedTrade?.tradeStatus && <Status tradeStatus={selectedTrade.tradeStatus} />}
                <span className="font-medium">{selectedTrade?.externalTradeId}</span>
              </div>
              {selectedTrade && selectedItems.length > 0 && (
                <RelatedOrderItems
                  platform={selectedTrade.platform}
                  unit={selectedTrade.unit}
                  items={selectedItems}
                />
              )}
              {isHisense && selectedTrade && (
                <div className="text-[var(--fett-text-secondary)] text-xs mt-1">
                  {t('TMS运单号')}: {extShipmentId || '—'}
                </div>
              )}
            </div>
            <div className=" text-[var(--fett-text-tertiary)] text-xs flex-shrink-0 flex items-center gap-2">
              {canEdit && (
                <CloseCircleFilled
                  onClick={handleClearOrder}
                  className="!hidden group-hover:!inline-block cursor-pointer hover:opacity-80"
                />
              )}
              {canEdit && <DownOutlined className={visible ? 'rotate-180' : ''} />}
            </div>
          </div>
        ) : (
          <div
            ref={ref}
            className={cx(
              'w-full border border-solid border-[var(--fett-border-strong)] h-[36px] flex items-center px-3 rounded-sm text-[var(--fett-text-quaternary)] min-w-0',
              canEdit && 'cursor-pointer',
            )}
            onClick={() => canEdit && setVisible(true)}
          >
            <span className="flex-1 truncate">
              {t('department.请选择x', { x: t('tab.trade') })}
            </span>
            {canEdit && <DownOutlined className="text-xs" />}
          </div>
        )}
      </Popover>
    </Panel>
  );
};

const popCls = css`
  .ant-popover-placement-bottom,
  .ant-popover-placement-bottomLeft,
  .ant-popover-placement-bottomRight {
    padding: 0;
  }
  .ant-popover-inner-content {
    padding: 0;
  }
  .ant-popover-arrow {
    display: none;
  }
`;
